import useAlias from '@alias';
import CautionIcon from '@assets/caution.svg?react';
import FireIcon from '@assets/fire.svg?react';
import WarningIcon from '@assets/warning.svg?react';
import CloseIcon from '@mui/icons-material/Close';
import { Button, Stack } from '@mui/material';
import {
  GridFilterModel,
  GridToolbarContainer,
  GridToolbarExport,
  GridToolbarQuickFilter,
  useGridApiContext,
} from '@mui/x-data-grid-pro';
import { DateTimePicker } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { LocalizationProvider } from '@mui/x-date-pickers-pro/LocalizationProvider';
import FormattedAlias from '@src/components/_shared/Translations/FormattedAlias';
import storeConnector from '@src/store/storeConnector';
import { Alert } from '@src/types/Alerts';
import { SiteMeta } from '@src/types/SiteMeta';
import { Moment } from 'moment';
import { useMemo } from 'react';
import FilterSelect, { FilterSelectOption } from './Filters/FilterSelect';
import {
  extractUnitName,
  handleAlarmNameOptionChange,
  handleDateTimeChange,
  handleDeviceOptionChange,
  handleSeverityOptionChange,
  resetFilterModel,
} from './Filters/filterUtils';
import GridToolbarAcknowledgeButton from './GridToolbarAcknowledgeButton';

interface ActiveAlertsGridToolbarProps {
  filterModel: GridFilterModel;
  siteMeta: SiteMeta;
}

function ActiveAlertsGridToolbar({
  filterModel,
  siteMeta,
}: ActiveAlertsGridToolbarProps) {
  const alias = useAlias();
  const iconButtons = [
    {
      icon: <CautionIcon className='icon active' />,
      severity: 'Warning',
    },
    {
      icon: <WarningIcon className='icon active' />,
      severity: 'Alarm',
    },
    {
      icon: <FireIcon className='icon active' />,
      severity: 'Fire',
    },
  ];

  const apiRef = useGridApiContext();
  const currentRows = apiRef.current?.getSortedRows();

  const upsertFilterItems = apiRef.current?.upsertFilterItems;
  const defaultDeviceAndAlarm = useMemo(
    () => ({
      defaultDevice: {
        label: alias('Any Device'),
        value: '',
      },

      defaultAlertName: {
        label: alias('Any Alarm'),
        value: '',
      },
    }),
    [alias],
  );

  const deviceAndNameFilterOptions = useMemo(
    () =>
      currentRows.reduce(
        (acc, curr) => {
          if (curr === undefined) {
            return acc;
          }
          const deviceName = extractUnitName(curr as Alert, siteMeta);
          const alarmName = curr.config?.alertName;

          if (
            !acc.deviceOptions.some(
              (option: FilterSelectOption) => option.value === deviceName,
            )
          ) {
            acc.deviceOptions.push({
              label: deviceName,
              value: deviceName,
            });
          }
          if (
            alarmName &&
            !acc.alarmNames.some(
              (option: FilterSelectOption) => option.value === alarmName,
            )
          ) {
            acc.alarmNames.push({
              label: alarmName,
              value: alarmName,
            });
          }
          return acc;
        },
        {
          deviceOptions: [defaultDeviceAndAlarm.defaultDevice],
          alarmNames: [defaultDeviceAndAlarm.defaultAlertName],
        },
      ),
    [
      currentRows,
      siteMeta,
      defaultDeviceAndAlarm.defaultDevice,
      defaultDeviceAndAlarm.defaultAlertName,
    ],
  );

  const {
    currentSeverity,
    currentDeviceName,
    currentAlarmName,
    currentStartDate,
    currentEndDate,
  } = useMemo(
    () =>
      filterModel.items.reduce(
        (acc, item) => {
          if (item.field === 'severity') {
            acc.currentSeverity = item.value;
          }
          if (item.field === 'unitName') {
            acc.currentDeviceName = {
              label:
                item.value === ''
                  ? defaultDeviceAndAlarm.defaultDevice.label
                  : item.value,
              value: item.value,
            };
          }
          if (item.field === 'alertName') {
            acc.currentAlarmName = {
              label:
                item.value === ''
                  ? defaultDeviceAndAlarm.defaultAlertName.label
                  : item.value,
              value: item.value,
            };
          }
          if (item.field === 'ts' && item.operator === 'onOrAfter') {
            acc.currentStartDate = item.value;
          }
          if (item.field === 'ts' && item.operator === 'onOrBefore') {
            acc.currentEndDate = item.value;
          }
          return acc;
        },
        {
          currentSeverity: undefined,
          currentDeviceName: defaultDeviceAndAlarm.defaultDevice,
          currentAlarmName: defaultDeviceAndAlarm.defaultAlertName,
          currentStartDate: null,
          currentEndDate: null,
        },
      ),
    [
      filterModel.items,
      defaultDeviceAndAlarm.defaultDevice,
      defaultDeviceAndAlarm.defaultAlertName,
    ],
  );

  return (
    <GridToolbarContainer>
      <Stack
        flex='1'
        sx={{
          p: 1.5,
          mb: 3,
        }}
        spacing={4}
      >
        <Stack direction='row' flex='1' justifyContent={'space-between'}>
          <Stack direction='row' spacing={2}>
            {iconButtons.map(({ icon, severity }) => (
              <Button
                key={severity}
                variant={
                  currentSeverity === severity ? 'contained' : 'outlined'
                }
                color={currentSeverity === severity ? 'primary' : 'inherit'}
                className='icon-button'
                startIcon={icon}
                size='small'
                sx={{
                  width: 125,
                  '.MuiButton-startIcon': {
                    width: 24,
                  },
                }}
                onClick={() =>
                  handleSeverityOptionChange(
                    severity,
                    filterModel,
                    upsertFilterItems,
                  )
                }
              >
                <FormattedAlias id={severity} />
              </Button>
            ))}
            <Button
              variant={!currentSeverity ? 'contained' : 'outlined'}
              color={!currentSeverity ? 'primary' : 'inherit'}
              className='icon-button'
              size='small'
              sx={{
                width: 125,
              }}
              onClick={() =>
                handleSeverityOptionChange(
                  'ALL',
                  filterModel,
                  upsertFilterItems,
                )
              }
            >
              <FormattedAlias id={'All'} />
            </Button>
          </Stack>
          <Stack direction='row' spacing={2}>
            {!process.env.VITE_READ_ONLY_MODE && (
              <GridToolbarAcknowledgeButton />
            )}
            <GridToolbarExport
              slotProps={{
                button: {
                  variant: 'contained',
                  color: 'primary',
                  className: 'export-button',
                  size: 'large',
                },
              }}
            />
          </Stack>
        </Stack>

        <Stack direction='row' spacing={4} alignItems='flex-end'>
          <FilterSelect
            currentValue={
              currentDeviceName || defaultDeviceAndAlarm.defaultDevice
            }
            options={deviceAndNameFilterOptions.deviceOptions}
            handleChange={(e) =>
              handleDeviceOptionChange(e, filterModel, upsertFilterItems)
            }
          />
          <FilterSelect
            currentValue={
              currentAlarmName || defaultDeviceAndAlarm.defaultAlertName
            }
            options={deviceAndNameFilterOptions.alarmNames}
            handleChange={(e) =>
              handleAlarmNameOptionChange(e, filterModel, upsertFilterItems)
            }
          />
          <LocalizationProvider dateAdapter={AdapterMoment}>
            <DateTimePicker
              label={alias('Start Date/Time')}
              value={currentStartDate}
              onChange={(newValue) =>
                handleDateTimeChange(
                  newValue as Moment,
                  'onOrAfter',
                  filterModel,
                  upsertFilterItems,
                )
              }
              sx={{
                width: 150,
              }}
              slotProps={{
                textField: {
                  variant: 'standard',
                  size: 'small',
                },
              }}
            />
            <DateTimePicker
              label={alias('End Date/Time')}
              value={currentEndDate}
              onChange={(newValue) =>
                handleDateTimeChange(
                  newValue as Moment,
                  'onOrBefore',
                  filterModel,
                  upsertFilterItems,
                )
              }
              sx={{
                width: 150,
              }}
              slotProps={{
                textField: {
                  variant: 'standard',
                  size: 'small',
                },
              }}
            />
          </LocalizationProvider>
          <GridToolbarQuickFilter
            slotProps={{
              input: {
                placeholder: alias('Description search'),
              },
            }}
            sx={{
              pb: 0,
            }}
          />
          <Button
            variant='outlined'
            color='inherit'
            size='small'
            startIcon={<CloseIcon />}
            sx={{
              width: 150,
              ml: 2,
            }}
            onClick={() => {
              apiRef.current?.setFilterModel({ items: [] });
              upsertFilterItems(resetFilterModel.items);
            }}
          >
            <FormattedAlias id={'Reset'} />
          </Button>
        </Stack>
      </Stack>
    </GridToolbarContainer>
  );
}

export default storeConnector(ActiveAlertsGridToolbar, {
  config: ['siteMeta'],
});
